/** useByteSelection — drag/click/shift-click byte range selection for the hex editor. */
import { useCallback, useMemo,useRef, useState } from 'react';

export interface ByteRange { start: number; end: number }

/** Resolve the `data-byte-index` attribute from an event target.
 *  Falls back to elementFromPoint when pointer capture redirects e.target
 *  to the capturing container instead of the byte span under the cursor. */
function byteIndexFromEvent(e: React.PointerEvent | React.MouseEvent): number | null {
  let el = (e.target as HTMLElement).closest?.('[data-byte-index]');
  if (!el) {
    const under = document.elementFromPoint(e.clientX, e.clientY);
    el = under?.closest?.('[data-byte-index]') ?? null;
  }
  if (!el) return null;
  const v = Number(el.getAttribute('data-byte-index'));
  return Number.isFinite(v) ? v : null;
}

export function useByteSelection() {
  const [anchorIndex, setAnchorIndex] = useState<number | null>(null);
  const [focusIndex, setFocusIndex] = useState<number | null>(null);
  const isDragging = useRef(false);
  const didDrag = useRef(false);
  const anchorRef = useRef<number | null>(null);

  const selection: ByteRange | null = useMemo(() => {
    if (anchorIndex === null || focusIndex === null) return null;
    return { start: Math.min(anchorIndex, focusIndex), end: Math.max(anchorIndex, focusIndex) };
  }, [anchorIndex, focusIndex]);

  const isSelected = useCallback(
    (idx: number) => selection !== null && idx >= selection.start && idx <= selection.end,
    [selection],
  );

  const setSelection = useCallback((anchor: number, focus?: number) => {
    setAnchorIndex(anchor);
    setFocusIndex(focus ?? anchor);
  }, []);

  const clearSelection = useCallback(() => {
    setAnchorIndex(null);
    setFocusIndex(null);
  }, []);

  /** Pointer-down: begin potential drag. */
  const handlePointerDown = useCallback((e: React.PointerEvent) => {
    const idx = byteIndexFromEvent(e);
    if (idx === null) return;

    if (e.shiftKey && anchorIndex !== null) {
      setFocusIndex(idx);
      didDrag.current = false;
      return;
    }

    setAnchorIndex(idx);
    setFocusIndex(idx);
    isDragging.current = true;
    didDrag.current = false;
    anchorRef.current = idx;
    (e.currentTarget as HTMLElement).setPointerCapture(e.pointerId);
  }, [anchorIndex]);

  /** Pointer-move: extend selection if dragging. */
  const handlePointerMove = useCallback((e: React.PointerEvent) => {
    if (!isDragging.current) return;
    const idx = byteIndexFromEvent(e);
    if (idx !== null) {
      setFocusIndex(idx);
      if (idx !== anchorRef.current) didDrag.current = true;
    }
  }, []);

  /** Pointer-up: finalise drag. Returns true if this was a single click (no drag). */
  const handlePointerUp = useCallback((e: React.PointerEvent) => {
    if (!isDragging.current) return false;
    isDragging.current = false;
    (e.currentTarget as HTMLElement).releasePointerCapture(e.pointerId);
    return !didDrag.current;
  }, []);

  return {
    selection, anchorIndex, isSelected, setSelection, clearSelection,
    handlePointerDown, handlePointerMove, handlePointerUp,
  };
}
